import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { usePunditUserContext } from "@circle-react/contexts";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import { ChatView } from "../components/ChatView";
import { SkeletonLoader } from "../components/common/SkeletonLoader";
import { useHelpWidgetContext } from "../context/HelpWidgetContext";
import { useCreateChat } from "../hooks/useCreateChat";
import { useCreateChatMessage } from "../hooks/useCreateChatMessage";
import { useShowConversation } from "../hooks/useShowConversation";
import type { HelpWidgetMessage } from "../types/help_widget_message";

export const ChatPage = ({ id }: { id?: string }) => {
  const { currentCommunityMember } = usePunditUserContext();
  const { isExpanded, isSidebarOpen } = useHelpWidgetContext();
  const location = useLocation<{ initialText?: string }>();
  const history = useHistory();
  const initialText = location.state?.initialText;
  const { mutate: createChat } = useCreateChat({
    onError: error => {
      console.error("Error creating chat", error);
    },
    onSuccess: conversation => {
      history.replace(`/chats/${conversation.id}`);
    },
  });

  useEffect(() => {
    if (initialText) {
      createChat({
        text: initialText,
      });
    }
  }, [createChat, initialText]);

  const { data: conversation, isLoading } = useShowConversation(id);

  const [messages, setMessages] = useState<HelpWidgetMessage[]>(
    () => conversation?.messages || [],
  );

  useEffect(() => {
    if (conversation?.messages && messages.length === 0) {
      setMessages(conversation.messages);
    }
  }, [conversation?.messages, messages.length]);

  const { mutate: createChatMessage } = useCreateChatMessage({
    onError: error => {
      console.error("Error creating chat message", error);
    },
    onSuccess: () => {},
  });

  useWebSocket(
    {
      channel: "HelpWidget::ConversationChannel",
      onMessageReceive: (data: {
        event: string;
        message: HelpWidgetMessage;
      }) => {
        if (data.event === "newMessage") {
          setMessages(prevMessages => [...prevMessages, data.message]);
        }
      },
      conversation_id: id,
      canCreateConnection: Boolean(id),
    },
    [id],
  );

  const handleSendMessage = (text: string) => {
    if (!id) {
      createChat({
        text,
      });
    } else {
      createChatMessage({
        conversationId: id,
        text,
      });
    }
    setMessages(prevMessages => [
      ...prevMessages,
      {
        id: `${Date.now().toString(36)}-${Math.random().toString(36).substring(2)}`,
        content: text,
        author_name: currentCommunityMember?.name ?? "",
        author_type: "user",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ]);
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full flex-1 flex-col px-4">
        <SkeletonLoader rows={4} height="52px" wrapperClassName="!px-0" />
      </div>
    );
  }

  if (conversation) {
    return (
      <div className="scrollbar-hide flex h-full flex-col overflow-y-auto">
        <div className="flex h-full w-full">
          <div
            className={classNames(
              "flex-1 transition-all duration-200 ease-in-out",
              {
                "w-[calc(100%-392px)]": isSidebarOpen && isExpanded,
                "w-full": !isSidebarOpen,
              },
            )}
          >
            <ChatView messages={messages} onSendMessage={handleSendMessage} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="scrollbar-hide flex h-full flex-col overflow-y-auto">
      <div className="flex h-full w-full">
        <div
          className={classNames(
            "flex-1 transition-all duration-200 ease-in-out",
            {
              "w-[calc(100%-392px)]": isSidebarOpen && isExpanded,
              "w-full": !isSidebarOpen,
            },
          )}
        >
          <ChatView messages={[]} onSendMessage={handleSendMessage} />
        </div>
      </div>
    </div>
  );
};
